import { useCallback, useEffect, useRef } from "react";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { FitAddon } from "@xterm/addon-fit";
import { api } from "../../api";
import type { DevelopmentSelection } from "../chat/ChatDevelopmentPanel.types";
import { selectionBody } from "../chat/ChatDevelopmentPanel.types";

export function TerminalTab({
  conversationId,
  selection,
  command,
  onConnectionChange,
  onOutput,
  onExit,
}: {
  conversationId: string;
  selection: DevelopmentSelection;
  command?: { id: number; command: string; raw?: boolean };
  onConnectionChange?: (
    state: "connecting" | "connected" | "disconnected" | "error",
  ) => void;
  onOutput?: (text: string) => void;
  onExit?: (code: number) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const activeSocket = useRef<WebSocket | undefined>(undefined);
  const lastCommand = useRef<number | undefined>(undefined);
  const pendingCommand = useRef(command);
  const callbacks = useRef({ onConnectionChange, onOutput, onExit });
  useEffect(() => {
    callbacks.current = { onConnectionChange, onOutput, onExit };
  }, [onConnectionChange, onOutput, onExit]);
  const flushCommand = useCallback(() => {
    const next = pendingCommand.current;
    const socket = activeSocket.current;
    if (
      !next ||
      next.id === lastCommand.current ||
      socket?.readyState !== WebSocket.OPEN
    )
      return;
    socket.send(
      JSON.stringify({
        type: "input",
        data: next.raw ? next.command : `${next.command}\r`,
      }),
    );
    lastCommand.current = next.id;
  }, []);
  useEffect(() => {
    pendingCommand.current = command;
    flushCommand();
  }, [command, flushCommand]);
  useEffect(() => {
    if (!host.current) return;
    callbacks.current.onConnectionChange?.("connecting");
    const terminal = new Terminal({
      cursorBlink: true,
      convertEol: true,
      fontSize: 13,
      theme: {
        background: "#0c0f0e",
        foreground: "#e7e5e4",
        cursor: "#34d399",
      },
    });
    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.open(host.current);
    fit.fit();
    terminal.focus();
    let socket: WebSocket | undefined;
    let disposed = false;
    let terminalError = false;
    void api<{ ticket: string }>(
      `/conversations/${conversationId}/development-workspace/terminal-ticket`,
      { method: "POST", body: JSON.stringify(selectionBody(selection)) },
    )
      .then(({ ticket }) => {
        if (disposed) return;
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        socket = new WebSocket(
          `${protocol}//${window.location.host}/api/development-terminal?ticket=${encodeURIComponent(ticket)}`,
        );
        activeSocket.current = socket;
        socket.addEventListener("open", () => {
          if (disposed) return;
          fit.fit();
          socket?.send(
            JSON.stringify({
              type: "resize",
              cols: terminal.cols,
              rows: terminal.rows,
            }),
          );
          callbacks.current.onConnectionChange?.("connected");
          flushCommand();
        });
        socket.addEventListener("message", (event) => {
          if (disposed) return;
          const raw = String(event.data);
          if (!raw) return;
          try {
            const message = JSON.parse(raw);
            if (message.type === "ready") {
              // The initial open event can precede the server's PTY attach.
              fit.fit();
              socket?.send(
                JSON.stringify({
                  type: "resize",
                  cols: terminal.cols,
                  rows: terminal.rows,
                }),
              );
            }
            if (message.type === "data") {
              terminal.write(message.data);
              callbacks.current.onOutput?.(message.data);
            }
            if (message.type === "exit") {
              terminal.writeln(`\r\n[process exited ${message.exitCode}]`);
              callbacks.current.onExit?.(Number(message.exitCode));
            }
            if (message.type === "error") {
              terminalError = true;
              callbacks.current.onConnectionChange?.("error");
              terminal.writeln(`\r\n[terminal error: ${message.message}]`);
            }
          } catch {
            terminal.writeln(
              "\r\n[terminal received an invalid server message]",
            );
          }
        });
        socket.addEventListener("close", (event) => {
          if (!disposed) callbacks.current.onConnectionChange?.("disconnected");
          if (!disposed && !terminalError)
            terminal.writeln(
              `\r\n[terminal disconnected${event.code !== 1000 ? ` (${event.code}${event.reason ? `: ${event.reason}` : ""})` : ""}]`,
            );
        });
      })
      .catch((error) => {
        if (disposed) return;
        callbacks.current.onConnectionChange?.("error");
        terminal.writeln(
          `\r\n${error instanceof Error ? error.message : "Terminal unavailable"}`,
        );
      });
    const input = terminal.onData(
      (data) =>
        socket?.readyState === WebSocket.OPEN &&
        socket.send(JSON.stringify({ type: "input", data })),
    );
    const observer = new ResizeObserver(() => {
      fit.fit();
      if (socket?.readyState === WebSocket.OPEN)
        socket.send(
          JSON.stringify({
            type: "resize",
            cols: terminal.cols,
            rows: terminal.rows,
          }),
        );
    });
    observer.observe(host.current);
    return () => {
      disposed = true;
      if (activeSocket.current === socket) activeSocket.current = undefined;
      observer.disconnect();
      input.dispose();
      socket?.close();
      terminal.dispose();
    };
  }, [conversationId, selection, flushCommand]);
  return (
    <div className="h-full min-h-0 overflow-hidden bg-[#0c0f0e] p-2">
      {/* FitAddon measures this element, so keep padding on its wrapper. */}
      <div ref={host} className="h-full min-h-0 min-w-0" />
    </div>
  );
}
